// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import axios, { InternalAxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { getAccessToken, refreshAccessToken, clearAccessToken, hasAccessToken, lastRefreshFailureReason } from '../auth/auth-store';
import { setLogoutReason } from '../auth/logout-reason';
import { API_BASE_URL } from './base-url';

const api = axios.create({
  baseURL: API_BASE_URL
});

// Track if we're currently refreshing to prevent infinite loops
let isRefreshing = false;

// Resolver/rejector types for queued requests
type QueuedRequestResolver = (value: AxiosResponse | Promise<AxiosResponse>) => void;
type QueuedRequestRejector = (error: AxiosError) => void;
// Type for headers with set method
type HeadersWithSet = { set: (key: string, value: string) => void };

// Queue of requests waiting for token refresh
let failedQueue: Array<{
  resolve: QueuedRequestResolver;
  reject: QueuedRequestRejector;
  config: InternalAxiosRequestConfig;
}> = [];

// Process queued requests after refresh
const processQueue = (success: boolean) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (success) {
      // Retry with new token - resolve with the promise
      resolve(api(config));
    } else {
      reject(new Error('Token refresh failed') as unknown as AxiosError);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  try {
    // Get access token from memory (W3 hybrid architecture)
    const accessToken = getAccessToken();

    // Initialize headers if missing
    const headers = config.headers || (config.headers = {} as InternalAxiosRequestConfig['headers']);

    if (accessToken) {
      // Use Bearer token for JWT authentication (V4: JWT-only auth)
      if (headers && typeof (headers as Record<string, unknown>).set === 'function') {
        (headers as HeadersWithSet).set('Authorization', `Bearer ${accessToken}`);
      }
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
    }
  } catch {
    // Silently fail if unavailable (SSR, etc.)
  }

  return config;
});

// The key SessionContext persists under; the durable "this user signed in" signal.
const SESSION_KEY = 'clubview_session';

/** True when a session is stored, regardless of whether an access token is held. */
function hasStoredSession(): boolean {
  try {
    return window.localStorage?.getItem(SESSION_KEY) != null;
  } catch {
    return false;
  }
}

// Response interceptor to handle 401 with automatic refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh for 401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Don't retry refresh endpoint itself or if already retried
    if (originalRequest.url?.includes('/auth/refresh') || originalRequest._retry) {
      // Refresh failed or already retried, redirect to login
      handleAuthFailure();
      return Promise.reject(error);
    }

    // Only refresh if this user was authenticated. The in-memory access token is
    // not that test: auth-store self-clears it 30s before expiry, so by the time a
    // 401 arrives it is routinely gone, and treating that as "never authenticated"
    // is what made every call fail until a full page reload (A102). The stored
    // session is the durable signal — the httpOnly refresh cookie is invisible to JS.
    if (!hasAccessToken() && !hasStoredSession()) {
      // User was never authenticated, don't try to refresh
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    // Mark as retrying and start refresh
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const success = await refreshAccessToken();
      isRefreshing = false;

      if (success) {
        // Process queued requests
        processQueue(true);
        // Retry original request with new token
        return api(originalRequest);
      } else {
        // Refresh failed
        processQueue(false);
        handleAuthFailure();
        return Promise.reject(error);
      }
    } catch {
      isRefreshing = false;
      processQueue(false);
      handleAuthFailure();
      return Promise.reject(error);
    }
  }
);

/**
 * Handle authentication failure - clear tokens and redirect to login
 */
function handleAuthFailure(): void {
  try {
    // Clear memory token
    clearAccessToken();

    // Drop the stored session too, or the next 401 would try to refresh again on
    // the strength of a session the server has already refused (A102).
    try {
      window.localStorage?.removeItem(SESSION_KEY);
    } catch {
      // storage unavailable — nothing to clear
    }

    // Tell the login page why, when the server gave a reason. Inactivity is not the
    // same as an ordinary expiry and should not read as a generic error (W74).
    // sessionStorage, not the URL: it survives the full-page redirect below and
    // keeps the reason out of browser history and any referrer.
    const reason = lastRefreshFailureReason();
    if (reason) setLogoutReason(reason);

    // Redirect to login if not already there
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  } catch {
    // Silently fail
  }
}

export default api;
